import { useChatContext } from "@/contexts/chatContext";
import { useMyUser } from "@/hooks/useMyUser";
import { useMutation, useQuery } from "@apollo/client/react";
import GiftedChatAvatar from "@repo/components/src/shared/chat/giftedChat/avatar";
import {
  GiftedChat,
  InputToolbar,
} from "@repo/components/src/shared/chat/giftedChat/interop";
import GiftedChatMessage from "@repo/components/src/shared/chat/giftedChat/message";
import GiftedChatSend from "@repo/components/src/shared/chat/giftedChat/send";
import useOnSend from "@repo/components/src/shared/chat/giftedChat/send/useOnSend";
import GiftedChatSystemMessage from "@repo/components/src/shared/chat/giftedChat/systemMessage";
import { MontuMessage } from "@repo/components/src/shared/chat/types";
import {
  Avatar,
  AvatarFallbackText,
  AvatarGroup,
  AvatarImage,
} from "@repo/components/src/ui/avatar";
import { Box } from "@repo/components/src/ui/box";
import { Button, ButtonIcon } from "@repo/components/src/ui/button";
import { Heading } from "@repo/components/src/ui/heading";
import { HStack } from "@repo/components/src/ui/hstack";
import { Icon } from "@repo/components/src/ui/icon";
import { Pressable } from "@repo/components/src/ui/pressable";
import { Skeleton } from "@repo/components/src/ui/skeleton";
import { Spinner } from "@repo/components/src/ui/spinner";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import formatBytesToHuman from "@repo/components/src/utils/bytes";
import { BrandReadAllChatMessagesDocument } from "@repo/graphql/documents/chat/mutation.generated";
import {
  ChatMessagesDocument,
  GetChatDocument,
} from "@repo/graphql/documents/chat/query.generated";
import {
  MontuChatChatTypeChoices,
  MontuChatMessageMessageTypeChoices,
} from "@repo/graphql/gql/graphql";
import { DocumentPickerAsset } from "expo-document-picker";
import {
  ArrowLeft,
  FileText,
  Send as SendIcon,
  Sparkles,
  X,
} from "lucide-react-native";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import GiftedChatActions from "./actions";
import GiftedChatBubble from "./bubble";
import GiftedChatComposer from "./composer";

type Props = {
  selectedChatId: string;
  showHeader?: boolean;
};

const ListChatMessages = React.memo((props: Props) => {
  const { selectedChatId } = props;
  const { setSelectedChatId } = useChatContext();
  const [readMessages] = useMutation(BrandReadAllChatMessagesDocument);
  const chatMessagesQueryVariables = {
    chat_Id: selectedChatId,
    orderBy: "-id",
    first: 20,
  };
  const [documentPicker, setDocumentPicker] =
    useState<DocumentPickerAsset | null>(null);
  const { data: chatData } = useQuery(GetChatDocument, {
    variables: { id: selectedChatId },
  });
  const chat = chatData?.chat.get;
  const brand = chatData?.chat.get.brand;
  useEffect(() => {
    chat &&
      brand &&
      chat.unreadByBrand > 0 &&
      readMessages({
        variables: {
          chatId: chat.id,
          brand_Id: brand.id,
          unreadByBrand_Gte: 1,
        },
      });
  }, [readMessages, chat, brand]);
  const brandImage = brand?.image;
  const { data, loading, fetchMore } = useQuery(ChatMessagesDocument, {
    variables: chatMessagesQueryVariables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network",
  });
  const chatUser = chatData?.chat.get.chatparticipantSet[0]?.user;
  const { user: myUser } = useMyUser();
  const onSend = useOnSend({
    chatId: selectedChatId,
    sentByBrand: true,
    chatMessagesQueryVariables,
    onCompleted: () => {
      setDocumentPicker(null);
    },
    myUser,
  });
  const isGroup = chat?.chatType.enum === MontuChatChatTypeChoices.Group;
  const profilePicture = isGroup ? brand?.image : chatUser?.profilePicture;
  const chatName = isGroup ? chat.groupName : chatUser?.fullName;
  const chatSubtitle = isGroup
    ? `${chatData?.chat.get.chatparticipantSet.length ?? 0} miembros`
    : "";
  const pageInfo = data?.chat.chatMessages.pageInfo;
  const messageBrandUser = useMemo(
    () => ({
      _id: brand ? `${brand.__typename}:${brand.id}` : "BrandType",
      name: brand?.name || "",
      avatar: brandImage?.url,
    }),
    [brand, brandImage],
  );
  const messages: MontuMessage[] = useMemo(
    () =>
      data?.chat.chatMessages.edges.map((edge) => {
        const message = edge.node;
        const createdBy = edge.node.createdBy;
        const createdByImage = createdBy?.profilePicture;
        const upload = message.upload;
        const user = message.sentByBrand
          ? messageBrandUser
          : {
              _id: createdBy
                ? `${createdBy.__typename}:${createdBy.id}`
                : "UserType",
              name: createdBy?.fullName || "",
              avatar: createdByImage?.url,
            };
        return {
          _id: message.id,
          text: message.message,
          createdAt: message.createdAt
            ? new Date(message.createdAt).getTime()
            : Date.now(),
          sent: message.sent,
          received: message.sent,
          pending: !message.sent,
          messageRead: !message.unreadByUser,
          system: !createdBy,
          image:
            message.messageType.enum ===
            MontuChatMessageMessageTypeChoices.Image
              ? upload?.url || ""
              : undefined,
          video:
            message.messageType.enum ===
            MontuChatMessageMessageTypeChoices.Video
              ? upload?.url || ""
              : undefined,
          pdf:
            message.messageType.enum ===
              MontuChatMessageMessageTypeChoices.Pdf && upload
              ? {
                  uri: upload.url,
                  name: upload.name,
                  fileSize: message.uploadSize ?? 0,
                }
              : undefined,
          audioFile:
            message.messageType.enum ===
              MontuChatMessageMessageTypeChoices.Audio && upload
              ? { uri: upload.url, name: upload.name }
              : undefined,
          file:
            upload &&
            (
              [
                MontuChatMessageMessageTypeChoices.Document,
                MontuChatMessageMessageTypeChoices.Archive,
                MontuChatMessageMessageTypeChoices.Other,
              ] as string[]
            ).includes(message.messageType.enum)
              ? {
                  uri: upload.url,
                  name: upload.name,
                  mimeType: message.messageType.display,
                }
              : undefined,
          user: user,
        };
      }) || [],
    [data, messageBrandUser],
  );

  const isInitialLoading = loading && !data;

  const renderGroupAvatars = useCallback(() => {
    if (!chatData) return [];
    const avatars: React.ReactElement[] = [];
    if (chatData.chat.get.chatparticipantSet.length > 3) {
      avatars.push(
        <Avatar size="sm" className="bg-brand-500" key={"remaining-count"}>
          <Text className="text-typography-0" size="xs" bold>
            {"+" + (chatData.chat.get.chatparticipantSet.length - 3)}
          </Text>
        </Avatar>,
      );
    }
    const restAvatars = chatData?.chat.get.chatparticipantSet
      .slice(0, 3)
      .map((participant) => {
        const user = participant.user;
        const profilePicture = user?.profilePicture;
        return (
          <Avatar key={user.id} size="sm">
            <AvatarFallbackText>{user.fullName}</AvatarFallbackText>
            <AvatarImage source={{ uri: profilePicture?.url }} />
          </Avatar>
        );
      });
    return avatars.concat(restAvatars);
  }, [chatData]);

  const renderSkeletonMessage = useCallback(
    (align: "left" | "right", widthClass: string, key: string) => (
      <HStack
        key={key}
        space="sm"
        className={`items-end ${
          align === "right" ? "flex-row-reverse" : "flex-row"
        }`}
      >
        <Skeleton className="h-9 w-9 rounded-full" />
        <VStack space="xs" className={align === "right" ? "items-end" : ""}>
          <Skeleton className={`h-3 w-20 rounded-md`} />
          <Skeleton className={`h-14 ${widthClass} rounded-2xl`} />
        </VStack>
      </HStack>
    ),
    [],
  );

  const renderLoadingSkeleton = useCallback(
    () => (
      <VStack space="lg" className="flex-1 justify-end px-4 pb-4">
        {renderSkeletonMessage("left", "w-52", "sk-1")}
        {renderSkeletonMessage("right", "w-40", "sk-2")}
        {renderSkeletonMessage("left", "w-64", "sk-3")}
        {renderSkeletonMessage("right", "w-32", "sk-4")}
      </VStack>
    ),
    [renderSkeletonMessage],
  );

  const renderChatEmpty = useCallback(
    () => (
      <VStack
        space="md"
        className="flex-1 items-center justify-center px-8 py-16 scale-y-[-1]"
      >
        <Box className="relative">
          <Box className="absolute inset-0 rounded-[28px] bg-brand-500/20 blur-2xl" />
          <Box className="relative p-5 rounded-2xl bg-brand-500 shadow-[0_10px_30px_-6px_rgba(124,58,237,0.55)]">
            <Icon as={Sparkles} className="text-background-0 w-8 h-8" />
          </Box>
        </Box>
        <VStack space="xs" className="items-center">
          <Heading size="md" className="text-center">
            Rompamos el hielo
          </Heading>
          <Text size="sm" action="muted" className="text-center max-w-xs">
            Saluda a {chatName || "este chat"} y empieza a crear junt@s.
          </Text>
        </VStack>
      </VStack>
    ),
    [chatName],
  );

  return (
    <VStack className="flex-1 relative bg-background-0">
      <Box
        pointerEvents="none"
        className="absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(1000px 400px at 80% -10%, rgba(124,58,237,0.08) 0%, transparent 60%), radial-gradient(800px 300px at 0% 110%, rgba(244,114,182,0.07) 0%, transparent 60%)",
        }}
      />
      {props.showHeader !== false && (
        <HStack
          space="md"
          className="relative items-center px-4 md:px-5 py-3 md:py-4 bg-background-0/80 backdrop-blur-md border-b border-outline-100"
        >
          <Button
            action="muted"
            variant="link"
            size="sm"
            className="md:hidden -ml-1"
            onPress={() => setSelectedChatId(null)}
          >
            <ButtonIcon as={ArrowLeft} />
          </Button>
          <Box className="relative">
            <Avatar size="md" key={chat?.id}>
              <AvatarFallbackText>{chatName}</AvatarFallbackText>
              <AvatarImage source={{ uri: profilePicture?.url }} />
            </Avatar>
            {!isGroup && (
              <Box className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-success-500 border-2 border-background-0" />
            )}
          </Box>
          <VStack className="flex-1 shrink">
            <Heading size="sm" bold isTruncated numberOfLines={1}>
              {chatName}
            </Heading>
            <Text
              size="xs"
              action="muted"
              className="tracking-wide"
              isTruncated
              numberOfLines={1}
            >
              {chatSubtitle}
            </Text>
          </VStack>
          {isGroup && (
            <AvatarGroup className="flex-row">
              {renderGroupAvatars()}
            </AvatarGroup>
          )}
        </HStack>
      )}
      <Box className="flex-1 relative">
        <GiftedChat
          messages={messages}
          user={messageBrandUser}
          textInputProps={{ placeholder: "Escribe un mensaje..." }}
          locale="es"
          isDayAnimationEnabled={true}
          isUsernameVisible
          messagesContainerClassName="px-0 md:px-4 py-0 md:py-0"
          minComposerHeight={40}
          renderChatEmpty={
            isInitialLoading ? renderLoadingSkeleton : renderChatEmpty
          }
          loadEarlierMessagesProps={{
            isAvailable: !!pageInfo?.hasNextPage && !loading,
            onPress: () => {
              pageInfo &&
                fetchMore({
                  variables: {
                    after: pageInfo.endCursor,
                  },
                });
            },
            isInfiniteScrollEnabled: true,
            isLoading: loading,
          }}
          renderLoadEarlier={() =>
            loading && !isInitialLoading ? (
              <HStack space="sm" className="items-center justify-center py-3">
                <Spinner size="small" className="text-brand-500" />
                <Text size="xs" action="muted" className="tracking-wide">
                  Loading messages…
                </Text>
              </HStack>
            ) : null
          }
          isSendButtonAlwaysVisible
          isAvatarOnTop={false}
          isUserAvatarVisible={false}
          listProps={{ keyboardShouldPersistTaps: "never" }}
          renderAvatar={null}
          renderBubble={(props) => <GiftedChatBubble {...props} />}
          renderMessage={(props) => (
            <GiftedChatMessage {...props} showAlwaysOnLeft={false} />
          )}
          renderDay={(props) => {
            if (!props.createdAt) return null;
            const date = new Date(props.createdAt);
            const now = new Date();
            const startOfDay = (d: Date) =>
              new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
            const diffDays = Math.round(
              (startOfDay(now) - startOfDay(date)) / 86400000,
            );
            let label: string;
            if (diffDays === 0) label = "hoy";
            else if (diffDays === 1) label = "ayer";
            else if (diffDays === -1) label = "mañana";
            else if (diffDays > 1 && diffDays <= 6)
              label = new Intl.DateTimeFormat("es", {
                weekday: "long",
              }).format(date);
            else
              label = new Intl.DateTimeFormat("es", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
                .format(date)
                .replace(/\./g, "");
            return (
              <Box className="items-center justify-center my-3">
                <Box className="px-3 py-1 rounded-full bg-background-100 border border-outline-100">
                  <Text
                    size="2xs"
                    action="muted"
                    bold
                    className="uppercase tracking-[0.15em] web:select-text"
                  >
                    {label}
                  </Text>
                </Box>
              </Box>
            );
          }}
          renderSystemMessage={(props) => (
            <GiftedChatSystemMessage {...props} />
          )}
          onSend={onSend}
          renderSend={(props) => {
            return (
              <GiftedChatSend
                {...props}
                documentPicker={documentPicker}
                buttonProps={{
                  rounded: "full",
                  size: "circle-md",
                  className:
                    "ml-1 shadow-[0_6px_16px_-6px_rgba(124,58,237,0.55)] data-[disabled=true]:shadow-none transition-all",
                  children: (
                    <ButtonIcon
                      as={SendIcon}
                      className="stroke-none fill-current"
                    />
                  ),
                  action: "brand",
                  variant: "solid",
                }}
              />
            );
          }}
          renderActions={(props) => {
            return (
              <GiftedChatActions
                {...props}
                setDocumentPicker={setDocumentPicker}
              />
            );
          }}
          renderComposer={(props) => (
            <GiftedChatComposer {...props} documentPicker={documentPicker} />
          )}
          renderAccessory={
            documentPicker
              ? () => (
                  <HStack
                    space="sm"
                    className="items-center mx-4 mb-2 px-3 py-2 rounded-xl bg-background-0 border border-outline-100"
                  >
                    <Box className="p-2 rounded-lg bg-brand-50">
                      <Icon as={FileText} className="text-brand-500 w-4 h-4" />
                    </Box>
                    <VStack className="flex-1 shrink">
                      <Text size="sm" bold isTruncated numberOfLines={1}>
                        {documentPicker.name}
                      </Text>
                      <Text size="xs" action="muted">
                        {formatBytesToHuman(documentPicker.size || 0)}
                      </Text>
                    </VStack>
                    <Pressable
                      onPress={() => setDocumentPicker(null)}
                      className="p-1.5 rounded-full"
                    >
                      <Icon as={X} size="sm" action="muted" />
                    </Pressable>
                  </HStack>
                )
              : undefined
          }
          renderInputToolbar={(props) => (
            <InputToolbar
              {...props}
              primaryClassName="px-1 py-1 md:px-3 md:py-3 items-center bg-transparent gap-2"
              className="border-t border-t-outline-50 bg-background-0 backdrop-blur-md"
            />
          )}
        />
      </Box>
    </VStack>
  );
});

ListChatMessages.displayName = "ListChatMessages";

export default ListChatMessages;
